import React, { useEffect, useState } from 'react';
import {
  Container,
  Flex,
  ActionIcon,
  Divider,
  Text,
  Button,
  Group,
  Menu,
  Modal,
  Table,
  MenuItem,
  Card,
  Stack,
  Tooltip
} from '@mantine/core';
import IndividualResponseCard from '../components/ResponsePage/IndividualResponseCard';
import ResponseCard from '../components/ResponsePage/ResponseCard';
import { IconFileSpreadsheet, IconArrowLeft } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { ToastMessages, DefaultTexts, typeTexts } from './messages/ResponseTexts';
import BarChart from '../components/bar';
import { URLs } from './messages/apiUrls';
import { useNavigate } from 'react-router-dom';
import { formatFileAnswer } from './formatFileAnswer';

const ResponsePage = () => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [individualResponses, setIndividualResponses] = useState([]);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [type, setType] = useState('Questions');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ID = params.get('formId');
  const [id, setId] = useState('');
  const [formName, setFormName] = useState('');

  function getTokenFromCookie() {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith('token='));
    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    }
    return null;
  }

  const fetchDecryptedId = async (encryptedId) => {
    try {
      const token = getTokenFromCookie();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/decryptId`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ encryptedId }),
      });

      if (!response.ok) {
        throw new Error('Failed to decrypt ID');
      }

      const decryptedData = await response.json();
      return decryptedData.decryptedId;
    } catch (error) {
      console.error('Error decrypting ID:', error);
      throw error;
    }
  };

const fetchFormName = async (formId) => {
  const token = getTokenFromCookie();
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/ArrayForm?id=${formId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) throw new Error("Failed to fetch form");

    const data = await response.json();
    setFormName(data.formName || "Untitled Form");
  } catch (error) {
    console.error("Error fetching form name:", error);
    setFormName("Untitled Form");
  }
};

  async function fetchResponses(formId) {
    const token = getTokenFromCookie();
    try {
      const response = await fetch(URLs.RESPONSES, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formId: formId }),
      });

      if (response.ok) {
        const data = await response.json();
        const submissions = data.data || {};
        const groupedResponses = Object.entries(submissions).map(([name, items]) => ({
          name,
          title: items[0]?.title || 'Untitled',
          answers: Array.isArray(items)
            ? items.map((item) => item.answer).filter((answer) => answer !== undefined)
            : [],
          type: items[0]?.type || '-default-text',
          timestamps: Array.isArray(items)
            ? items.map((item) => {
                const date = new Date(item?.timestamp || new Date());
                const datePart = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                });
                const yearPart = date.toLocaleDateString('en-US', {
                  year: 'numeric',
                });
                const timePart = date.toLocaleTimeString();
                return `${datePart} ${yearPart} [${timePart}]`;
              })
            : [],
        }));
        console.log('Grouped responses:', groupedResponses);
        setResponses(groupedResponses);
        setIsLoading(false);
      } else {
        console.error(ToastMessages.FETCH_RESPONSE_FAILURE);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(ToastMessages.FETCH_RESPONSE_ERROR, error);
      setIsLoading(false);
    }
  }

  const fetchId = async () => {
    try {
      const decryptedId = await fetchDecryptedId(ID);
      setId(decryptedId);
      await Promise.all([
          fetchResponses(decryptedId),
          fetchFormName(decryptedId)
      ]);
    } catch (error) {
      console.error('Error fetching ID:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ID) {
      fetchId();
    }
  }, [ID]);

  useEffect(() => {
  async function fetchIndividualResponses() {
    const token = getTokenFromCookie();
    try {
      const response = await fetch(URLs.INDIVIDUAL_RESPONSE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formId: id }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Individual responses data:', data.data);
        
        // Extract individual responses with timestamps
        // const processedResponses = data.data.map(item => ({
        //   timestamp: item.timestamp,
        //   answers: item.formData[0].answers
        // }));
        const processedResponses = data.data.map((submission, index) => ({
          submissionId: index + 1,
          timestamp: submission.timestamp,
          answers: submission.formData.flatMap(page => page.answers)
        }));

        console.log("///",
            individualResponses.map(r => r.answers.map(a => a.title))
          );

        setIndividualResponses(processedResponses);
      } else {
        console.error(ToastMessages.FETCH_INDIVIDUAL_RESPONSES_FAILURE);
      }
    } catch (error) {
      console.error(ToastMessages.FETCH_INDIVIDUAL_RESPONSES_ERROR, error);
    }
  }
  if (type === 'Individual' && id) {
    fetchIndividualResponses();
  }
}, [id, type]);

  // Replace the generateCSVContent function in ResponsePage.js with this updated version

const generateCSVContent = () => {
  // Backward-safe wrapper if needed elsewhere
  if (type === 'Individual' && individualResponses.length > 0) {
    return generateIndividualCSVWithFiles().csvContent;
  }
  if (responses.length > 0) {
    return generateAggregatedCSVWithFiles().csvContent;
  }
  console.log('No responses available for CSV generation');
  return '';
};

const formatTimestamp = (ts) => {
  const date = new Date(ts || new Date());

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const yearPart = date.toLocaleDateString('en-US', {
    year: 'numeric',
  });

  const timePart = date.toLocaleTimeString();

  return `${datePart} ${yearPart} [${timePart}]`;
};

const formatFileAnswerWithStore = (answer, responseIndex, localFileStore) => {
  if (!Array.isArray(answer)) return '';
  return answer
    .map((file, fileIndex) => {
      if (!file?.name) return '';
      const fileId = `${responseIndex}_${fileIndex}_${file.name}`;
      localFileStore[fileId] = file;
      if (file.type?.startsWith('image/')) return `[Image:${fileId}] ${file.name}`;
      if (file.type?.startsWith('audio/')) return `[Audio:${fileId}] ${file.name}`;
      if (file.type?.startsWith('video/')) return `[Video:${fileId}] ${file.name}`;
      return `[File:${fileId}] ${file.name}`;
    })
    .join('; ');
};

const generateIndividualCSVWithFiles = () => {
  if (individualResponses.length === 0) return { csvContent: '', fileStore: {} };
  const localFileStore = {};

  console.log('Individual responses structure:', individualResponses);
  
  const firstResponse = individualResponses[0];
  console.log('First response:', firstResponse);
  // Handle different possible data structures
  let answersArray;
  if (Array.isArray(firstResponse)) {
    answersArray = firstResponse;
  } else if (firstResponse && firstResponse.answers && Array.isArray(firstResponse.answers)) {
    answersArray = firstResponse.answers;
  } else if (firstResponse && firstResponse.formData && firstResponse.formData[0] && firstResponse.formData[0].answers) {
    answersArray = firstResponse.formData[0].answers;
  } else {
    console.error('Unable to find answers array in first response');
    return '';
  }

  if (!answersArray || answersArray.length === 0) return { csvContent: '', fileStore: {} };

  // Create headers
  const questionHeaders = answersArray.map(item => item.title || 'Untitled Question');
  const headers = ['Submission ID', 'Timestamp', ...questionHeaders];

  // Prepare CSV rows
  const csvRows = [headers.map(header => `"${header.replace(/"/g, '""')}"`).join(',')];

  // Process each individual response
  individualResponses.forEach((responseSet, responseIndex) => {
    const row = [
      responseIndex + 1, // Submission ID
      new Date().toISOString() // Timestamp fallback
    ];
    
    // Handle different possible data structures for each response
    let currentAnswers;
    if (Array.isArray(responseSet)) {
      currentAnswers = responseSet;
    } else if (responseSet && responseSet.answers && Array.isArray(responseSet.answers)) {
      currentAnswers = responseSet.answers;
    } else if (responseSet && responseSet.formData && responseSet.formData[0] && responseSet.formData[0].answers) {
      currentAnswers = responseSet.formData[0].answers;
    } else {
      currentAnswers = [];
    }
    
    currentAnswers.forEach((responseItem, answerIndex) => {
      let answer = responseItem.answer || '';

      if (responseItem.type === 'file' && Array.isArray(answer)) {
        const formatted = formatFileAnswerWithStore(answer, `${responseIndex}_${answerIndex}`, localFileStore);
        row.push(`"${formatted.replace(/"/g, '""')}"`);
        return;
      }

      if (responseItem.type === 'checkbox' && Array.isArray(answer)) {
        answer = answer.join('; ');
      } else if (typeof answer === 'object' && answer !== null) {
        answer = formatFileAnswer(answer);
      }

      row.push(`"${answer.toString().replace(/"/g, '""')}"`);
    });
    
    csvRows.push(row.join(','));
  });

  return { csvContent: csvRows.join('\n'), fileStore: localFileStore };
};

const generateAggregatedCSVWithFiles = () => {
  if (responses.length === 0) return { csvContent: '', fileStore: {} };
  console.log('responses', responses)
  const localFileStore = {};
  const questionTitles = responses.map(response => response.title);
  const headers = ['Response Number', 'Timestamp', ...questionTitles];

  // Instead of creating multiple rows, create one row per unique timestamp/submission
  // and combine multiple answers for the same question in a single cell
  
  // Group responses by timestamp first
  const responsesByTimestamp = {};
  
  responses.forEach((response, responseIndex) => {
  response.answers.forEach((answer, answerIndex) => {
      const rawTimestamp = response.timestamps?.[answerIndex];
      const timestamp = rawTimestamp?.includes('[')
          ? rawTimestamp
          : formatTimestamp(rawTimestamp);

    if (!responsesByTimestamp[timestamp]) {
      responsesByTimestamp[timestamp] = {};
    }
    
    // Handle different types of answers
    let processedAnswer = answer;
    // FILE UPLOAD TYPE
if (response.type === 'file' && Array.isArray(answer)) {
  processedAnswer = answer
    .map((file, fileIndex) => {
      if (!file?.name) return '';
      const fileId = `${responseIndex}_${fileIndex}_${file.name}`;
      localFileStore[fileId] = file;
      if (file.type?.startsWith('image/')) return `[Image:${fileId}] ${file.name}`;
      if (file.type?.startsWith('audio/')) return `[Audio:${fileId}] ${file.name}`;
      if (file.type?.startsWith('video/')) return `[Video:${fileId}] ${file.name}`;
      return `[File:${fileId}] ${file.name}`;
    })
    .join('; ');
}

// CHECKBOX
else if (response.type === 'checkbox' && Array.isArray(answer)) {
  processedAnswer = answer.join('; ');
}
else if (
        response.type === 'signature' || (typeof answer === 'string' && answer.startsWith('data:image'))
      ) {
        processedAnswer = 'Signed';
      }

// OTHER ARRAYS
else if (Array.isArray(answer)) {
  processedAnswer = answer.join('; ');
}

// FALLBACK
else {
  processedAnswer = answer?.toString() || '';
}

    
    // Combine multiple answers for the same question
    if (responsesByTimestamp[timestamp][responseIndex]) {
      responsesByTimestamp[timestamp][responseIndex] += `; ${processedAnswer}`;
    } else {
      responsesByTimestamp[timestamp][responseIndex] = processedAnswer;
    }
  });
});

  // Create CSV rows
  const csvRows = [headers.map(header => `"${header.replace(/"/g, '""')}"`).join(',')];
  
  Object.entries(responsesByTimestamp).forEach(([timestamp, questionAnswers], index) => {
    const row = [index + 1, timestamp];
    
    questionTitles.forEach((title, index) => {
      const answer = questionAnswers[index] || '';
      let formattedAnswer = '';
      const responseObj = responses[index];
      const responseType = responseObj ? responseObj.type : '';
      
      if (responseType === 'checkbox' && Array.isArray(answer)) {
        formattedAnswer = answer.join('; ');
      } else if (Array.isArray(answer)) {
         formattedAnswer = answer.join('; ');
  } else if (Array.isArray(answer)) {
  formattedAnswer = answer
    .map(file => {
      if (!file?.name) return '';
      if (file.type?.startsWith('image/')) return `[Image] ${file.name}`;
      if (file.type?.startsWith('audio/')) return `[Audio] ${file.name}`;
      if (file.type?.startsWith('video/')) return `[Video] ${file.name}`;
      return `[File] ${file.name}`;
    })
    .join('; ');
}
 else {
    formattedAnswer = answer.toString();
  }
      row.push(`"${formattedAnswer.replace(/"/g, '""')}"`);
    });
    
    csvRows.push(row.join(','));
  });

  return { csvContent: csvRows.join('\n'), fileStore: localFileStore };
};


const viewCSV = async () => {
  try {
    let csvContent = '';
    let fileStore = {};

    // If Individual view and data not loaded
    if (type === 'Individual' && individualResponses.length === 0) {
      const token = getTokenFromCookie();

      const response = await fetch(URLs.INDIVIDUAL_RESPONSE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formId: id }),
      });

      if (!response.ok) {
        console.error('Failed to fetch individual responses');
        return;
      }

      const data = await response.json();
      const processedResponses = data.data[0].formData.map(
        (item) => item.answers
      );

      setIndividualResponses(processedResponses);

      // Generate CSV after state update
      setTimeout(() => {
        const result = generateIndividualCSVWithFiles();
        csvContent = result.csvContent;
        fileStore = result.fileStore;
        navigate(`/csv-table?formId=${encodeURIComponent(ID || id)}`, {
          state: { csvContent, fileStore },
        });
      }, 100);

    } else {
      // Data already present
      if (type === 'Individual') {
        const result = generateIndividualCSVWithFiles();
        csvContent = result.csvContent;
        fileStore = result.fileStore;
      } else {
        const result = generateAggregatedCSVWithFiles();
        csvContent = result.csvContent;
        fileStore = result.fileStore;
      }
      navigate(`/csv-table?formId=${encodeURIComponent(ID || id)}`, {
        state: { csvContent, fileStore },
      });
    }
  } catch (error) {
    console.error('Error viewing CSV:', error);
  }
};


 // Updated getChartData function in ResponsePage.js
const truncateText = (text, maxLength = 30) => {
  if (text === null || text === undefined) return "";

  // Arrays → join
  if (Array.isArray(text)) {
    text = text.join(", ");
  }

  // Objects → stringify safely
  if (typeof text === "object") {
    // file upload / image picker
    if (text.name) return truncateText(text.name, maxLength);
    return truncateText(JSON.stringify(text), maxLength);
  }

  // Numbers / booleans → convert
  if (typeof text !== "string") {
    text = String(text);
  }

  return text.length > maxLength
    ? text.substring(0, maxLength) + "..."
    : text;
};

// Utility function to create short labels for ranking
const createRankingLabel = (answer, index) => {
  if (Array.isArray(answer)) {
    // Create shortened version with just keywords
    const shortOptions = answer.map(option => {
      const parts = option.split(' - ');
      const mainTitle = parts[0];
      // Take first 2-3 words of the main title
      const shortTitle = mainTitle.split(' ').slice(0, 2).join(' ');
      return shortTitle;
    });
    return `Rank ${index + 1}: ${shortOptions.join(' > ')}`;
  } else if (typeof answer === 'object' && answer !== null) {
    const sortedEntries = Object.entries(answer).sort(([a], [b]) => parseInt(a) - parseInt(b));
    const shortOptions = sortedEntries.map(([rank, option]) => {
      const parts = option.split(' - ');
      const mainTitle = parts[0];
      const shortTitle = mainTitle.split(' ').slice(0, 2).join(' ');
      return `${rank}:${shortTitle}`;
    });
    return `Rank ${index + 1}: ${shortOptions.join(' > ')}`;
  }
  return `Ranking ${index + 1}`;
};

// Updated getChartData function in ResponsePage.js
const getChartData = (response) => {
  const answerCounts = {};
  const originalLabels = {}; // Store original labels for tooltips
  
  // Check if this is a ranking type response
  if (response.type === 'ranking') {
    // Handle ranking responses
    response.answers.forEach((answer, index) => {
      // Create a short label for display
      const shortLabel = createRankingLabel(answer, index);
      
      // Create full label for tooltip
      let fullLabel;
      if (Array.isArray(answer)) {
        fullLabel = answer.join(' > ');
      } else if (typeof answer === 'object' && answer !== null) {
        const sortedEntries = Object.entries(answer).sort(([a], [b]) => parseInt(a) - parseInt(b));
        fullLabel = sortedEntries.map(([rank, option]) => `${rank}: ${option}`).join(' > ');
      } else {
        fullLabel = answer.toString();
      }
      
      // Check if this exact ranking order already exists
      let existingKey = Object.keys(answerCounts).find(key => originalLabels[key] === fullLabel);
      
      if (existingKey) {
        answerCounts[existingKey] = (answerCounts[existingKey] || 0) + 1;
      } else {
        answerCounts[shortLabel] = (answerCounts[shortLabel] || 0) + 1;
        originalLabels[shortLabel] = fullLabel;
      }
    });
  } else if (response.type === 'checkbox') {
    // Handle checkbox responses (multiple selections)
    response.answers.forEach((answer) => {
      if (Array.isArray(answer)) {
        // For checkbox, each selected option should be counted separately
        answer.forEach((option) => {
          const shortLabel = truncateText(option);
          answerCounts[shortLabel] = (answerCounts[shortLabel] || 0) + 1;
          originalLabels[shortLabel] = option;
        });
      } else {
        const shortLabel = truncateText(answer);
        answerCounts[shortLabel] = (answerCounts[shortLabel] || 0) + 1;
        originalLabels[shortLabel] = answer;
      }
    });
  } else {
    // Handle other response types (radio, dropdown, etc.)
    response.answers.forEach((answer) => {
      let answerKey;
      
      if (Array.isArray(answer)) {
        answerKey = answer.join(', ');
      } else if (typeof answer === 'object' && answer !== null) {
        answerKey = JSON.stringify(answer);
      } else {
        answerKey = answer;
      }
      
      const shortLabel = truncateText(answerKey);
      answerCounts[shortLabel] = (answerCounts[shortLabel] || 0) + 1;
      originalLabels[shortLabel] = answerKey;
    });
  }

  return {
    labels: Object.keys(answerCounts),
    datasets: [
      {
        label: `Count for "${response.title}"`,
        data: Object.values(answerCounts),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
    originalLabels, // Pass original labels for tooltips
  };
};

  const handleNextResponse = () => {
    if (currentResponseIndex < individualResponses.length - 1) {
      setCurrentResponseIndex(currentResponseIndex + 1);
    }
  };

  const handlePreviousResponse = () => {
    if (currentResponseIndex > 0) {
      setCurrentResponseIndex(currentResponseIndex - 1);
    }
  };

  return (
    <div style={{ background: '#e6f9f4', minHeight: '100vh' }}>
      <Container fluid p="md" bg="#ffffff" >
        <Flex direction="row" align="center" justify="space-between" wrap="nowrap">

          <Group spacing="md">
            <Button
              variant="light"
              leftSection={<IconArrowLeft size='12px' />}
              onClick={() => navigate('/')}
              color="dark"
              radius="md"
              style={{ marginRight: '190px', outline: 'none' }}
            >
              Back
            </Button>
            <Text fw={600} fz="lg" color="dark" style={{textTransform: 'capitalize'}}>
            {isLoading
                      ? DefaultTexts.LOADING_RESPONSES
                      : `${DefaultTexts.RESPONSES}${formName ? ` for '${formName}'` : ''}`}
            </Text>
          </Group>

          <Group spacing="xs">
            {['Questions', 'Individual', 'Summary'].map((btn) => (
              <Button
                key={btn}
                variant={type === btn ? 'filled' : 'outline'}
                onClick={() => setType(btn)}
                radius="md"
              >
                {btn}
              </Button>
            ))}
          {((type === 'Questions' && responses.length > 0) ||
            (type === 'Individual' && individualResponses.length > 0) ||
            (type === 'Summary' && responses.length > 0)) && (
            // <Menu shadow="md" width={200} position="bottom-end" offset={28} withinPortal>
            //   <Menu.Target>
            //     <ActionIcon variant="filled" color="dark" size="lg" radius="md">
            //       <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            //     </ActionIcon>
            //   </Menu.Target>
            //   <Menu.Dropdown style={{ background: '#ffffffff' }}>
            //     <Menu.Item leftSection={<IconDownload size={14} stroke={2} />} onClick={downloadCSV}>
            //       Download CSV
            //     </Menu.Item>
            //     <Menu.Item leftSection={<IconEye size={14} stroke={2} />} onClick={viewInWeb}>
            //       View
            //     </Menu.Item>
            //   </Menu.Dropdown>
            // </Menu>         
            <Tooltip label="CSV" withArrow>
              <ActionIcon
                  variant="filled"
                  color="dark"
                  size="lg"
                  radius="md"
                  onClick={viewCSV}
                  aria-label='CSV'     >
                  <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            </Tooltip>

            )}
          </Group>
        </Flex>
      </Container>
      <Divider color="gray" />

      <Container size="lg" py="xl">
        {isLoading ? (
          <Flex justify="center" align="center" direction="column" style={{ height: '50vh' }}>
            <Text fw={500} fz="lg" color="dimmed">{DefaultTexts.LOADING}</Text>
          </Flex>
        ) : (
          <Stack spacing="xl">
            {type === typeTexts.questions && (
              responses.length === 0 ? (
                <Card shadow="sm" radius="md" withBorder style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text fz="md" color="dimmed">{DefaultTexts.NO_RESPONSES}</Text>
                </Card>
              ) : (
                <Stack spacing="md" gap='xs'>
                  {responses.map((response, index) => (
                      <ResponseCard
                        key={response.name}
                        title={response.title}
                        responseJson={response.answers ? response.answers : []}
                        type={response.type}
                      />
                  ))}
                </Stack>
              )
            )}

            {type === typeTexts.summary && (
              responses.length === 0 ? (
                <Card shadow="sm" radius="md" withBorder style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text fz="md" color="dimmed">{DefaultTexts.NO_RESPONSES}</Text>
                </Card>
              ) : (
              <Stack spacing="md">
                {responses.map((response, index) => (
                  <div key={index}>
                    {response.type !== 'text' && response.type !== 'comment' && (
                        <BarChart
                          key={index}
                          data={getChartData(response)}
                          flag={response.type === 'dropdown'}
                        />
                    )}
                  </div>
                ))}
              </Stack>)
            )}

            {type === typeTexts.individualType && (
              individualResponses.length === 0 ? (
                <Card shadow="sm" radius="md" withBorder style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text fz="md" color="dimmed">{DefaultTexts.NO_RESPONSES}</Text>
                </Card>
              ) : (
                <Stack spacing="sm" gap='xs'>
                    {individualResponses[currentResponseIndex] &&
                      individualResponses[currentResponseIndex].answers.map((response, index) => (
                        <IndividualResponseCard
                          key={index}
                          title={response.title}
                          answer={response.answer}
                        />
                      ))}

                  <Group position="center" mt="md" justify='center'>
                    <Button
                      onClick={handlePreviousResponse}
                      disabled={currentResponseIndex === 0}
                    >
                      Previous
                    </Button>
                    <Text fw={500} color="dimmed">
                      {currentResponseIndex + 1} of {individualResponses.length}
                    </Text>
                    <Button
                      onClick={handleNextResponse}
                      disabled={currentResponseIndex === individualResponses.length - 1}
                    >
                      Next
                    </Button>
                  </Group>
                </Stack>
              )
            )}
          </Stack>
        )}
      </Container>
    </div>
  );
};

export default ResponsePage;
